import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@mds/shared/lib/utils';
import { Button } from '@mds/shared/ui/shadcn/button';
import { ImageCarousel, type ImageCarouselProps } from '@mds/shared/ui/image/image-carousel';

export type FeatureSplitProps = {
  eyebrow?: string;
  title?: string;
  description?: string;
  images?: ImageCarouselProps['images'];
  /**
   * With `bleed`, drawn in the frame instead of the photos: anything that
   * fills an absolutely positioned box. `images` is then not needed.
   */
  visual?: ReactNode;
  /**
   * `carousel` mounts the photos inside the panel's padding. `bleed` runs them
   * to the card's edges, crossfading (`ImageCarousel`'s `fill`): the top on a
   * phone, its half of the card from `lg`, as tall as the copy beside it.
   * `carousel` (the prop below) is ignored here.
   */
  media?: 'carousel' | 'bleed';
  carousel?: 'stack' | 'flat';
  galleryLabel?: string;
  imageSide?: 'left' | 'right';
  cta?: { label: string; to: string };
  headingLevel?: 2 | 3;
  /** Added to the title's classes, to adjust its size (`text-4xl`). */
  titleClassName?: string;
  interval?: number;
  children?: ReactNode;
  className?: string;
};

export default function FeatureSplit({
  eyebrow,
  title,
  description,
  images = [],
  visual,
  media = 'carousel',
  carousel = 'stack',
  galleryLabel,
  imageSide = 'right',
  cta,
  headingLevel = 2,
  titleClassName,
  interval,
  children,
  className,
}: FeatureSplitProps) {
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  const imageLeft = imageSide === 'left';
  const bleed = media === 'bleed';

  return (
    <div
      data-ground="panel"
      className={cn(
        'grid overflow-hidden rounded-card bg-panel text-on-panel lg:grid-cols-2',
        // A bled photo has no padding to sit in, so the padding moves onto the
        // copy and the two halves meet with no gap.
        bleed ? 'items-stretch' : 'items-center gap-block p-card lg:gap-16 lg:p-block',
        className,
      )}
    >
      <div
        className={cn(
          'flex flex-col items-start gap-6',
          bleed && 'p-card lg:p-block',
          imageLeft && 'lg:order-2',
        )}
      >
        {eyebrow && (
          <span className="font-mono text-2xs font-light text-brand uppercase">{eyebrow}</span>
        )}
        {/* The 15ch cap keeps a headline beside a carousel to two lines; a
            bled photo's copy column is narrower, and the cap would break a
            one-line title in two. */}
        {title && (
          <Heading className={cn('text-4xl', !bleed && 'max-w-[15ch]', titleClassName)}>
            {title}
          </Heading>
        )}
        {description && <p className="max-w-prose text-base text-on-panel/75">{description}</p>}

        {children}

        {cta && (
          <Button asChild variant="secondary" size="lg" className="mt-2">
            <Link to={cta.to}>{cta.label}</Link>
          </Button>
        )}
      </div>
      {bleed ? (
        // First on a phone, whichever side it takes from `lg`. 16:9 there;
        // from `lg` the frame has no height of its own and takes the row's,
        // which the copy beside it sets.
        <div
          className={cn(
            'relative aspect-video max-lg:order-first lg:aspect-auto',
            // The placeholder is for photos still loading; a visual fills the
            // frame itself, and the light ground would bleed at its edges.
            !visual && 'bg-placeholder',
            imageLeft && 'lg:order-1',
          )}
        >
          {visual ?? (
            <ImageCarousel
              variant="fill"
              label={galleryLabel ?? title ?? ''}
              images={images}
              interval={interval}
              className="absolute inset-0"
            />
          )}
        </div>
      ) : (
        <div
          className={cn(
            'mx-auto w-full',
            carousel === 'stack' && 'max-w-sm',
            imageLeft && 'lg:order-1',
          )}
        >
          <ImageCarousel
            variant={carousel}
            label={galleryLabel ?? title ?? ''}
            images={images}
            interval={interval}
          />
        </div>
      )}
    </div>
  );
}
